import type { ConsentKitConfig, ConsentRecord } from "../types";
import { readCookieJar, writeCookieJar } from "./native";
import { RECORD_VERSION } from "./defaults";

/**
 * The decision lives in a first-party cookie so it survives across subdomains and is
 * readable server-side; localStorage is a mirror for the case where cookies are cleared
 * by a third-party-cookie blocker but the origin's storage survives.
 *
 * The cookie is strictly necessary in the § 25 Abs. 2 TDDDG sense (it stores the choice
 * the user was asked to make), so it is exempt from consent itself.
 */
const MIRROR_KEY = "ck_consent_record";

export function loadRecord(config: ConsentKitConfig): ConsentRecord | null {
  const raw = readCookie(config.cookieName) ?? readMirror();
  if (!raw) return null;

  const record = parseRecord(raw);
  if (!record) return null;
  if (record.recordVersion !== RECORD_VERSION) return null;
  if (record.configVersion !== config.configVersion) return null;
  if (isExpired(record, config.consentLifetimeDays)) return null;

  return record;
}

export function saveRecord(config: ConsentKitConfig, record: ConsentRecord): void {
  const serialized = JSON.stringify(record);
  writeCookie(config, serialized);
  try {
    window.localStorage.setItem(MIRROR_KEY, serialized);
  } catch {
    /* storage disabled — the cookie is authoritative anyway */
  }
}

export function clearRecord(config: ConsentKitConfig): void {
  writeCookieJar(
    `${config.cookieName}=; Path=/; Max-Age=0; SameSite=Lax${domainPart(config)}${securePart()}`,
  );
  try {
    window.localStorage.removeItem(MIRROR_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Deletes cookies a revoked vendor left behind. Names may be exact ("_fbp") or a
 * prefix wildcard ("_ga*"). Only same-origin cookies can be removed from JS — a
 * third-party cookie set on the vendor's own domain is out of reach, which is
 * precisely why blocking before the fact is the load-bearing mechanism.
 */
export function deleteCookiesMatching(patterns: string[]): void {
  const jar = readCookieJar();
  if (!jar) return;

  const names = jar
    .split(";")
    .map((pair) => pair.split("=")[0]?.trim())
    .filter((name): name is string => Boolean(name));

  const hosts = hostVariants();
  for (const name of names) {
    if (!patterns.some((pattern) => matchesPattern(name, pattern))) continue;
    for (const domain of hosts) {
      const domainAttr = domain ? `; Domain=${domain}` : "";
      writeCookieJar(`${name}=; Path=/; Max-Age=0${domainAttr}`);
    }
  }
}

function matchesPattern(name: string, pattern: string): boolean {
  if (pattern.endsWith("*")) return name.startsWith(pattern.slice(0, -1));
  return name === pattern;
}

/** "" (host-only), ".example.com", ".co.example.com" — cookies may be set at any level. */
function hostVariants(): string[] {
  const parts = window.location.hostname.split(".");
  const variants: string[] = [""];
  for (let i = 0; i < parts.length - 1; i += 1) {
    variants.push(`.${parts.slice(i).join(".")}`);
  }
  return variants;
}

function readCookie(name: string): string | null {
  const jar = readCookieJar();
  const prefix = `${name}=`;
  for (const pair of jar.split(";")) {
    const trimmed = pair.trim();
    if (trimmed.startsWith(prefix)) return decodeURIComponent(trimmed.slice(prefix.length));
  }
  return null;
}

function writeCookie(config: ConsentKitConfig, value: string): void {
  const maxAge = Math.round(config.consentLifetimeDays * 24 * 60 * 60);
  writeCookieJar(
    `${config.cookieName}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax` +
      `${domainPart(config)}${securePart()}`,
  );
}

function domainPart(config: ConsentKitConfig): string {
  return config.cookieDomain ? `; Domain=${config.cookieDomain}` : "";
}

function securePart(): string {
  return window.location.protocol === "https:" ? "; Secure" : "";
}

function readMirror(): string | null {
  try {
    return window.localStorage.getItem(MIRROR_KEY);
  } catch {
    return null;
  }
}

function parseRecord(raw: string): ConsentRecord | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const record = parsed as ConsentRecord;
    if (!record.decision || typeof record.decision !== "object") return null;
    if (typeof record.timestamp !== "string") return null;
    return record;
  } catch {
    return null;
  }
}

function isExpired(record: ConsentRecord, lifetimeDays: number): boolean {
  const decidedAt = Date.parse(record.timestamp);
  if (!Number.isFinite(decidedAt)) return true;
  return Date.now() - decidedAt > lifetimeDays * 24 * 60 * 60 * 1000;
}
